import React, { useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export interface ColumnDef<T> {
  key: string;
  label: string;
  render: (row: T) => React.ReactNode;
  headerRender?: () => React.ReactNode;
  hideOnMobile?: boolean;
  sortable?: boolean;
  type?: 'text' | 'number' | 'currency' | 'percent';
}

interface DataTableProps<T> {
  // Core
  columns: ColumnDef<T>[];
  data: T[];
  gridColumns: string;
  rowKey: (row: T) => string;

  // Optional behavior
  onRowClick?: (row: T) => void;
  emptyState?: React.ReactNode;
  noStriped?: boolean;
  noHover?: boolean;

  // Sort
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;

  // Multi-select
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;

  // Loading
  isLoading?: boolean;
  skeletonRows?: number;

  // Pagination
  pagination?: {
    page: number;
    pages: number;
    total?: number;
    onPageChange: (page: number) => void;
  };

  // Expandable
  expandable?: boolean;
  renderExpanded?: (row: T) => React.ReactNode;

  // Sticky header
  stickyHeader?: boolean;

  footer?: React.ReactNode;
}

function numericCellStyle(type?: string): React.CSSProperties {
  if (type === 'number' || type === 'currency' || type === 'percent') {
    return {
      textAlign: 'right',
      fontFamily: '"JetBrains Mono", monospace',
      fontVariantNumeric: 'tabular-nums',
    };
  }
  return {};
}

function SkeletonLine({ cols }: { cols: number }) {
  return (
    <div className="dt-row" style={{ pointerEvents: 'none', opacity: 0.5 }}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} style={{ padding: '0.25rem 0.5rem' }}>
          <div style={{ height: 12, background: 'rgba(255,255,255,0.06)', borderRadius: 'var(--radius-interactive)', width: `${60 + Math.random() * 30}%` }} />
        </div>
      ))}
    </div>
  );
}

function SortIcon({ field, sortBy, sortOrder }: { field: string; sortBy?: string; sortOrder?: string }) {
  if (sortBy !== field) return <ChevronsUpDown size={12} style={{ opacity: 0.3 }} />;
  return sortOrder === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
}

export function DataTable<T extends { id?: string }>({
  columns, data, gridColumns, rowKey, onRowClick,
  emptyState, noStriped, noHover,
  sortBy, sortOrder, onSort,
  selectable, selectedIds, onSelectionChange,
  isLoading, skeletonRows = 8,
  pagination,
  expandable, renderExpanded,
  stickyHeader,
  footer,
}: DataTableProps<T>) {
  const isMobile = useIsMobile();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // ─── Helpers ────────────────────────────────────────────────
  const toggleSelectAll = () => {
    if (!onSelectionChange || !selectable) return;
    const allIds = new Set(data.map(r => rowKey(r)));
    if (allIds.size > 0 && data.every(r => selectedIds?.has(rowKey(r)))) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(allIds);
    }
  };

  const toggleSelect = (id: string) => {
    if (!onSelectionChange || !selectable) return;
    const next = new Set(selectedIds ?? []);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectionChange(next);
  };

  const allSelected = selectable && data.length > 0 && data.every(r => selectedIds?.has(rowKey(r)));

  // ─── Empty / Loading ────────────────────────────────────────
  if (!isLoading && data.length === 0 && emptyState) return <>{emptyState}</>;

  // ─── Mobile card view ───────────────────────────────────────
  if (isMobile) {
    const visibleCols = columns.filter(c => !c.hideOnMobile);
    const actionCols = columns.filter(c => c.key === 'actions');

    return (
      <div className="dt-cards">
        {isLoading
          ? Array.from({ length: skeletonRows }).map((_, i) => (
              <div key={i} className="dt-card" style={{ opacity: 0.4 }}>
                <div style={{ height: 14, background: 'rgba(255,255,255,0.06)', borderRadius: 'var(--radius-interactive)', width: '70%' }} />
                <div style={{ height: 12, background: 'rgba(255,255,255,0.06)', borderRadius: 'var(--radius-interactive)', width: '50%' }} />
                <div style={{ height: 12, background: 'rgba(255,255,255,0.06)', borderRadius: 'var(--radius-interactive)', width: '60%' }} />
              </div>
            ))
          : data.map(row => (
              <div key={rowKey(row)} className="dt-card" onClick={() => onRowClick?.(row)}>
                {selectable && (
                  <div className="dt-card-row">
                    <input type="checkbox" className="dt-checkbox" checked={selectedIds?.has(rowKey(row)) ?? false} onChange={() => toggleSelect(rowKey(row))} onClick={e => e.stopPropagation()} />
                  </div>
                )}
                {visibleCols.map(col => (
                  <div key={col.key} className="dt-card-row">
                    <span className="dt-card-label">{col.label}</span>
                    <span className="dt-card-value" style={numericCellStyle(col.type)}>{col.render(row)}</span>
                  </div>
                ))}
                {expandable && renderExpanded && (
                  <div onClick={e => { e.stopPropagation(); setExpandedId(expandedId === rowKey(row) ? null : rowKey(row)); }} style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)', cursor: 'pointer', paddingTop: '0.25rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    {expandedId === rowKey(row) ? '▲ Nascondi' : '▼ Dettagli'}
                  </div>
                )}
                {expandedId === rowKey(row) && renderExpanded && (
                  <div style={{ paddingTop: '0.5rem' }}>{renderExpanded(row)}</div>
                )}
                {actionCols.map(col => (
                  <div key={col.key} className="dt-card-actions" onClick={e => e.stopPropagation()}>{col.render(row)}</div>
                ))}
              </div>
            ))}
        <style>{`
          .dt-cards { display: flex; flex-direction: column; gap: 0.5rem; }
          .dt-card {
            background: var(--glass-bg-card);
            backdrop-filter: blur(16px) saturate(140%);
            -webkit-backdrop-filter: blur(16px) saturate(140%);
            border: 1px solid rgba(255,255,255,0.10);
            border-radius: var(--radius-card);
            padding: 0.75rem 1rem;
            display: flex; flex-direction: column; gap: 0.5rem;
          }
          .dt-card-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 0.5rem; font-size: var(--text-sm); }
          .dt-card-label { color: var(--color-text-muted); flex-shrink: 0; min-width: 60px; }
          .dt-card-value { color: var(--color-text); text-align: right; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
          .dt-card-actions { display: flex; justify-content: flex-end; padding-top: 0.25rem; border-top: 1px solid rgba(255,255,255,0.08); }
        `}</style>
      </div>
    );
  }

  // ─── Desktop grid view ──────────────────────────────────────
  const colCount = columns.length;

  return (
    <div className="dt-root" style={stickyHeader ? { maxHeight: '70vh', overflowY: 'auto' } as React.CSSProperties : undefined}>
      {/* Header */}
      <div className={`dt-header ${stickyHeader ? 'dt-header--sticky' : ''}`}>
        {selectable && (
          <div key="_sel" style={{ display: 'flex', alignItems: 'center' }}>
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="dt-checkbox" />
          </div>
        )}
        {columns.map(col => (
          <div
            key={col.key}
            onClick={col.sortable && onSort ? () => onSort(col.key) : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.25rem',
              cursor: col.sortable && onSort ? 'pointer' : undefined,
              userSelect: 'none',
            }}
          >
            {col.headerRender ? col.headerRender() : (col.key === 'icon' || col.key === 'actions' ? '' : col.label)}
            {col.sortable && <SortIcon field={col.key} sortBy={sortBy} sortOrder={sortOrder} />}
          </div>
        ))}
      </div>

      {/* Body */}
      {isLoading
        ? Array.from({ length: skeletonRows }).map((_, i) => <SkeletonLine key={i} cols={colCount + (selectable ? 1 : 0)} />)
        : data.map((row, i) => (
            <React.Fragment key={rowKey(row)}>
              <div
                className={`dt-row ${!noStriped && i % 2 === 1 ? 'dt-row--striped' : ''} ${!noHover ? 'dt-row--hover' : ''} ${expandedId === rowKey(row) ? 'dt-row--expanded' : ''}`}
                onClick={() => onRowClick?.(row)}
              >
                {selectable && (
                  <div key="_sel" style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedIds?.has(rowKey(row)) ?? false} onChange={() => toggleSelect(rowKey(row))} className="dt-checkbox" />
                  </div>
                )}
                {expandable && (
                  <div key="_exp" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={e => { e.stopPropagation(); setExpandedId(expandedId === rowKey(row) ? null : rowKey(row)); }}>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{expandedId === rowKey(row) ? '▲' : '▼'}</span>
                  </div>
                )}
                {columns.map(col => (
                  <div key={col.key} style={numericCellStyle(col.type)}>{col.render(row)}</div>
                ))}
              </div>
              {/* Expanded detail */}
              {expandable && expandedId === rowKey(row) && renderExpanded && (
                <div className="dt-row-expanded">
                  {renderExpanded(row)}
                </div>
              )}
            </React.Fragment>
          ))}

      {/* Pagination footer */}
      {pagination && pagination.pages > 1 && (
        <div className="dt-pagination">
          <span className="dt-pagination-info">
            {pagination.total != null ? `${pagination.total} risultati · ` : ''}Pagina {pagination.page} di {pagination.pages}
          </span>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button className="dt-pagination-btn" onClick={() => pagination.onPageChange(1)} disabled={pagination.page <= 1}><ChevronsLeft size={14} /></button>
            <button className="dt-pagination-btn" onClick={() => pagination.onPageChange(pagination.page - 1)} disabled={pagination.page <= 1}><ChevronLeft size={14} /></button>
            <button className="dt-pagination-btn" onClick={() => pagination.onPageChange(pagination.page + 1)} disabled={pagination.page >= pagination.pages}><ChevronRight size={14} /></button>
            <button className="dt-pagination-btn" onClick={() => pagination.onPageChange(pagination.pages)} disabled={pagination.page >= pagination.pages}><ChevronsRight size={14} /></button>
          </div>
        </div>
      )}

      {footer}

      <style>{`
        /* ─── Root ──────────────────────────── */
        .dt-root {
          background: var(--glass-bg-card);
          backdrop-filter: blur(16px) saturate(140%);
          -webkit-backdrop-filter: blur(16px) saturate(140%);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: var(--radius-card);
          overflow: hidden;
        }

        /* ─── Header ────────────────────────── */
        .dt-header {
          display: grid;
          grid-template-columns: ${(selectable ? '32px ' : '') + (expandable ? '28px ' : '') + gridColumns};
          padding: 0.5625rem 1rem;
          background: rgba(255,255,255,0.04);
          font-size: 11px;
          font-weight: 600;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .dt-header--sticky {
          position: sticky;
          top: 0;
          z-index: 10;
        }

        /* ─── Rows ──────────────────────────── */
        .dt-row {
          display: grid;
          grid-template-columns: ${(selectable ? '32px ' : '') + (expandable ? '28px ' : '') + gridColumns};
          padding: 0.8125rem 1rem;
          font-size: var(--text-sm);
          border-bottom: 1px solid rgba(255,255,255,0.05);
          transition: background 0.12s ease;
          align-items: center;
        }
        .dt-row:last-child {
          border-bottom: none;
        }
        .dt-row--striped {
          background: rgba(255,255,255,0.02);
        }
        .dt-row--hover:hover {
          background: rgba(255,255,255,0.04);
        }
        .dt-row--expanded {
          background: rgba(255,255,255,0.02);
          border-bottom: none;
        }

        /* ─── Expanded detail ───────────────── */
        .dt-row-expanded {
          padding: 0.75rem 1rem 1rem;
          background: rgba(255,255,255,0.02);
          border-bottom: 1px solid rgba(255,255,255,0.05);
          font-size: var(--text-sm);
        }

        /* ─── Pagination ────────────────────── */
        .dt-pagination {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.625rem 1rem;
          border-top: 1px solid rgba(255,255,255,0.08);
          background: rgba(255,255,255,0.03);
          font-size: var(--text-xs);
          color: var(--color-text-muted);
        }
        .dt-pagination-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px; height: 28px;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: var(--radius-interactive);
          background: var(--glass-bg-card);
          color: var(--color-text);
          cursor: pointer;
          transition: all var(--spring-duration) var(--spring-easing);
        }
        .dt-pagination-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.06);
        }
        .dt-pagination-btn:disabled {
          opacity: 0.3;
          cursor: default;
        }
        .dt-pagination-info {
          font-size: var(--text-xs);
        }
      `}</style>
    </div>
  );
}
